import React, { useContext, useEffect, useState } from 'react'
import { Modal, Tag } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { AppSettings } from '../../config/app-settings'
import Loader from '../pages/loader'
import ReusableTable from '../../components/other/ReuseableTable'
import SearchAndFilterBar from '../../components/other/search/SearchAndFilterBar'
import EditCreateCollectorSchedule from '../../components/periodicTasks/EditCreateCollectorSchedule'
import { can } from '../../helpers/auth'
import {
  useGetCollectorSchedulesQuery,
  useGetAvailableCollectorTasksQuery,
  useDeleteCollectorScheduleByIdMutation,
  useTriggerCollectorScheduleByIdMutation
} from '../../redux/apis/utilsManagement/collectorSchedule/uMCollectorScheduleApiSlice'

// ponytail: accepts both the dotted django permission and the bare codename,
// since effective_permissions is stored bare everywhere else in this app.
const canPeriodicTask = action =>
  can(`django_celery_beat.${action}_periodictask`) || can(`${action}_periodictask`)

const scheduleText = record => {
  if (record?.crontab) {
    const c = record.crontab
    return `${c.minute} ${c.hour} ${c.day_of_month} ${c.month_of_year} ${c.day_of_week}`
  }
  if (record?.interval) return `every ${record.interval.every} ${record.interval.period}`
  return record?.schedule || '-'
}

// ponytail: list / available-tasks payloads may be bare arrays or wrapped in data/results
const unwrap = d => d?.data?.results || d?.results || d?.data || (Array.isArray(d) ? d : [])

const CollectorSchedule = React.memo(() => {
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 })
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({})

  const context = useContext(AppSettings)
  const [holder, setHolder] = useState()
  const [edit, setEdit] = useState(false)
  const [create, setCreate] = useState(false)

  const { data, isLoading, isFetching, error } = useGetCollectorSchedulesQuery({
    page: pagination.current,
    page_size: pagination.pageSize,
    ...(search ? { search } : {}),
    ...filters
  })
  const { data: tasksData } = useGetAvailableCollectorTasksQuery(undefined, {
    skip: !canPeriodicTask('add') && !canPeriodicTask('change')
  })
  const [
    deleteSchedule,
    { isLoading: isDeleting, error: deleteError }
  ] = useDeleteCollectorScheduleByIdMutation()
  const [
    triggerSchedule,
    { isLoading: isTriggering, error: triggerError }
  ] = useTriggerCollectorScheduleByIdMutation()

  const rows = unwrap(data)
  const tasks = unwrap(tasksData)
    .map(t => (typeof t === 'string' ? t : t?.name || t?.task))
    .filter(Boolean)

  const handleEdit = record => {
    setHolder(record)
    setEdit(true)
  }

  const handleTrigger = record => {
    Modal.confirm({
      title: 'Trigger Periodic Task',
      icon: <ExclamationCircleOutlined />,
      content: `Run "${record.name}" now with its saved args/kwargs?`,
      okText: 'Run Now',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await triggerSchedule(record.id).unwrap()
          toast.success('Periodic task triggered successfully!')
        } catch (err) {
          console.error(err)
        }
      }
    })
  }

  const handleDelete = record => {
    Modal.confirm({
      title: 'Delete Periodic Task',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${record.name}"?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteSchedule(record.id).unwrap()
          toast.success('Periodic task deleted successfully!')
        } catch (err) {
          console.error(err)
        }
      }
    })
  }

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  let columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      filter: true,
      filterType: 'text'
    },
    {
      title: 'Task',
      dataIndex: 'task',
      key: 'task',
      filter: true,
      filterType: 'select',
      filterOptions: tasks.map(t => ({ label: t, value: t }))
    },
    {
      title: 'Schedule',
      key: 'schedule',
      sorter: false,
      render: (text, record) => scheduleText(record)
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      filter: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Enabled', value: true },
        { label: 'Disabled', value: false }
      ],
      render: text => <Tag color={text ? 'green' : 'red'}>{text ? 'Enabled' : 'Disabled'}</Tag>
    },
    { title: 'Last Run At', dataIndex: 'last_run_at', key: 'last_run_at' },
    { title: 'Total Runs', dataIndex: 'total_run_count', key: 'total_run_count' },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      render: (text, record) => (
        <div className='flex gap-2'>
          {canPeriodicTask('change') && (
            <button className='btn btn-outline-theme' onClick={() => handleEdit(record)}>
              Edit
            </button>
          )}
          {canPeriodicTask('change') && (
            <button className='btn btn-outline-theme' onClick={() => handleTrigger(record)}>
              Trigger
            </button>
          )}
          {canPeriodicTask('delete') && (
            <button className='btn btn-outline-danger' onClick={() => handleDelete(record)}>
              Delete
            </button>
          )}
        </div>
      )
    }
  ]

  useEffect(() => {
    const failure = error || deleteError || triggerError
    if (failure?.data) {
      const errorMessages = Object.values(failure.data).flat()
      errorMessages.forEach(message => {
        toast.error(String(message))
      })
    }
  }, [error, deleteError, triggerError])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoading || isFetching || isDeleting || isTriggering} />

      {canPeriodicTask('add') && create && (
        <EditCreateCollectorSchedule
          state='create'
          open={create}
          setOpen={setCreate}
          record={null}
          tasks={tasks}
        />
      )}
      {canPeriodicTask('change') && edit && (
        <EditCreateCollectorSchedule
          state='edit'
          open={edit}
          setOpen={setEdit}
          record={holder}
          tasks={tasks}
        />
      )}

      <div className='flex'>
        <SearchAndFilterBar
          columns={columns}
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setPagination={setPagination}
        />
        <div className='ml-auto flex gap-4'>
          {canPeriodicTask('add') && (
            <button
              className='btn btn-outline-theme ml-auto mb-4'
              onClick={() => setCreate(true)}
            >
              Create Periodic Task
            </button>
          )}
        </div>
      </div>

      <div className='row'>
        <ReusableTable
          columns={columns}
          data={rows}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.data?.count ?? data?.count ?? rows.length,
            showSizeChanger: true
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  )
})

export default CollectorSchedule
